
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface Challenge {
  id: string
  title: string
  description: string
  category: string
  points: number
  icon: string
  color: string
  challenge_modules?: Array<{
    id: string
    module_id: string
    order_index: number
    learn_modules: {
      id: string
      title: string
    }
  }>
  user_progress?: {
    completed_modules: number
    total_modules: number
    is_completed: boolean
  }
}

export const useChallenges = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchChallenges = async () => {
    try {
      const { data, error } = await supabase
        .from('challenges')
        .select(`
          *,
          challenge_modules (
            id,
            module_id,
            order_index,
            learn_modules (
              id,
              title
            )
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      setChallenges(data || [])
    } catch (error) {
      console.error('Error fetching challenges:', error)
      toast({
        title: "Error",
        description: "Failed to fetch challenges",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchChallengesWithProgress = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        await fetchChallenges()
        return
      }

      const { data, error } = await supabase
        .from('challenges')
        .select(`
          *,
          challenge_modules (
            id,
            module_id,
            order_index,
            learn_modules (
              id,
              title
            )
          ),
          user_challenge_progress (
            completed_modules,
            total_modules,
            is_completed
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Transform the data to include user progress
      const challengesWithProgress = data?.map(challenge => ({
        ...challenge,
        user_progress: challenge.user_challenge_progress?.[0] || null
      })) || []

      setChallenges(challengesWithProgress)
    } catch (error) {
      console.error('Error fetching challenges with progress:', error)
      toast({
        title: "Error",
        description: "Failed to fetch challenges",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const createChallenge = async (challengeData: {
    title: string
    description: string
    category: string
    points: number
    icon: string
    color: string
    moduleIds: string[]
  }) => {
    try {
      // Create the challenge
      const { data: challenge, error: challengeError } = await supabase
        .from('challenges')
        .insert({
          title: challengeData.title,
          description: challengeData.description,
          category: challengeData.category,
          points: challengeData.points,
          icon: challengeData.icon,
          color: challengeData.color
        })
        .select()
        .single()

      if (challengeError) throw challengeError

      // Create challenge-module mappings
      const challengeModules = challengeData.moduleIds.map((moduleId, index) => ({
        challenge_id: challenge.id,
        module_id: moduleId,
        order_index: index + 1
      }))

      const { error: modulesError } = await supabase
        .from('challenge_modules')
        .insert(challengeModules)

      if (modulesError) throw modulesError

      toast({
        title: "Success",
        description: "Challenge created successfully"
      })

      await fetchChallenges()
    } catch (error) {
      console.error('Error creating challenge:', error)
      toast({
        title: "Error",
        description: "Failed to create challenge",
        variant: "destructive"
      })
    }
  }

  const updateChallenge = async (challengeId: string, challengeData: {
    title: string
    description: string
    category: string
    points: number
    icon: string
    color: string
    moduleIds: string[]
  }) => {
    try {
      // Update the challenge
      const { error: challengeError } = await supabase
        .from('challenges')
        .update({
          title: challengeData.title,
          description: challengeData.description,
          category: challengeData.category,
          points: challengeData.points,
          icon: challengeData.icon,
          color: challengeData.color
        })
        .eq('id', challengeId)

      if (challengeError) throw challengeError

      // Delete existing challenge-module mappings
      const { error: deleteError } = await supabase
        .from('challenge_modules')
        .delete()
        .eq('challenge_id', challengeId)

      if (deleteError) throw deleteError

      // Create new challenge-module mappings
      const challengeModules = challengeData.moduleIds.map((moduleId, index) => ({
        challenge_id: challengeId,
        module_id: moduleId,
        order_index: index + 1
      }))

      const { error: modulesError } = await supabase
        .from('challenge_modules')
        .insert(challengeModules)

      if (modulesError) throw modulesError

      toast({
        title: "Success",
        description: "Challenge updated successfully"
      })

      await fetchChallenges()
    } catch (error) {
      console.error('Error updating challenge:', error)
      toast({
        title: "Error",
        description: "Failed to update challenge",
        variant: "destructive"
      })
    }
  }

  const deleteChallenge = async (challengeId: string) => {
    try {
      const { error } = await supabase
        .from('challenges')
        .delete()
        .eq('id', challengeId)

      if (error) throw error

      toast({
        title: "Success",
        description: "Challenge deleted successfully"
      })

      await fetchChallenges()
    } catch (error) {
      console.error('Error deleting challenge:', error)
      toast({
        title: "Error",
        description: "Failed to delete challenge",
        variant: "destructive"
      })
    }
  }

  const getDashboardChallenges = () => {
    // Priority: challenges with 1+ completed modules, then random 2
    const challengesWithProgress = challenges.filter(c => 
      c.user_progress && c.user_progress.completed_modules > 0
    )
    
    const remainingChallenges = challenges.filter(c => 
      !c.user_progress || c.user_progress.completed_modules === 0
    )

    // Take up to 2 challenges with progress, then fill with random ones
    const selected = [
      ...challengesWithProgress.slice(0, 2),
      ...remainingChallenges.slice(0, 2 - challengesWithProgress.length)
    ]

    return selected.slice(0, 2)
  }

  useEffect(() => {
    fetchChallengesWithProgress()
  }, [])

  return {
    challenges,
    loading,
    createChallenge,
    updateChallenge,
    deleteChallenge,
    getDashboardChallenges,
    refetch: fetchChallengesWithProgress
  }
}
